/**
 * 邀请/佣金 Vue Composable
 * 封装 InviteService 的响应式接口
 */

import { ref, computed, readonly, type Ref, type ComputedRef } from 'vue'
import type { InviteService } from '../../../core/services/InviteService'
import type {
  RewardDetail,
  RecommendUser,
  RecommendFailLog,
  RewardItem,
  WithdrawRecord,
  PaginatedResponse,
  InviteInfo
} from '../../../types/invite'

export interface UseInviteReturn {
  // 状态
  rewardDetail: ComputedRef<RewardDetail | null>
  availableReward: ComputedRef<number>
  frozenReward: ComputedRef<number>
  totalReward: ComputedRef<number>
  canWithdraw: ComputedRef<boolean>
  loading: Readonly<Ref<boolean>>
  error: Readonly<Ref<Error | null>>

  // 方法
  getRewardDetail: () => Promise<RewardDetail | null>
  applyWithdraw: () => Promise<boolean>
  getRewardList: (page?: number, perPage?: number) => Promise<PaginatedResponse<RewardItem> | null>
  getRecommendFailList: (
    page?: number,
    perPage?: number
  ) => Promise<PaginatedResponse<RecommendFailLog> | null>
  getRecommendUserList: (
    page?: number,
    perPage?: number
  ) => Promise<PaginatedResponse<RecommendUser> | null>
  getWithdrawRecordList: (
    page?: number,
    perPage?: number
  ) => Promise<PaginatedResponse<WithdrawRecord> | null>
  getInviteInfo: () => Promise<InviteInfo | null>
}

/**
 * 创建 useInvite composable
 */
export function createUseInvite(inviteService: InviteService) {
  return function useInvite(): UseInviteReturn {
    const loading = ref(false)
    const error = ref<Error | null>(null)

    // 从 store 获取响应式状态（直接返回 store 的 computed，避免双重包装）
    const store = inviteService.getInviteStore()

    const rewardDetail = computed(() => store.rewardDetail)
    const availableReward = store.availableReward
    const frozenReward = store.frozenReward
    const totalReward = store.totalReward
    const canWithdraw = store.canWithdraw

    /**
     * 获取佣金详情
     */
    const getRewardDetail = async (): Promise<RewardDetail | null> => {
      loading.value = true
      error.value = null
      try {
        return await inviteService.getRewardDetail()
      } catch (err) {
        error.value = err as Error
        console.error('[useInvite] 获取佣金详情失败:', err)
        return null
      } finally {
        loading.value = false
      }
    }

    /**
     * 申请提现
     */
    const applyWithdraw = async (): Promise<boolean> => {
      loading.value = true
      error.value = null
      try {
        await inviteService.applyWithdraw()
        return true
      } catch (err) {
        error.value = err as Error
        console.error('[useInvite] 申请提现失败:', err)
        return false
      } finally {
        loading.value = false
      }
    }

    /**
     * 获取佣金列表
     */
    const getRewardList = async (
      page = 1,
      perPage = 10
    ): Promise<PaginatedResponse<RewardItem> | null> => {
      loading.value = true
      error.value = null
      try {
        return await inviteService.getRewardList(page, perPage)
      } catch (err) {
        error.value = err as Error
        console.error('[useInvite] 获取佣金列表失败:', err)
        return null
      } finally {
        loading.value = false
      }
    }

    /**
     * 获取推荐失败列表
     */
    const getRecommendFailList = async (
      page = 1,
      perPage = 10
    ): Promise<PaginatedResponse<RecommendFailLog> | null> => {
      loading.value = true
      error.value = null
      try {
        return await inviteService.getRecommendFailList(page, perPage)
      } catch (err) {
        error.value = err as Error
        console.error('[useInvite] 获取推荐失败列表失败:', err)
        return null
      } finally {
        loading.value = false
      }
    }

    /**
     * 获取推荐用户列表
     */
    const getRecommendUserList = async (
      page = 1,
      perPage = 10
    ): Promise<PaginatedResponse<RecommendUser> | null> => {
      loading.value = true
      error.value = null
      try {
        return await inviteService.getRecommendUserList(page, perPage)
      } catch (err) {
        error.value = err as Error
        console.error('[useInvite] 获取推荐用户列表失败:', err)
        return null
      } finally {
        loading.value = false
      }
    }

    /**
     * 获取提现记录
     */
    const getWithdrawRecordList = async (
      page = 1,
      perPage = 10
    ): Promise<PaginatedResponse<WithdrawRecord> | null> => {
      loading.value = true
      error.value = null
      try {
        return await inviteService.getWithdrawRecordList(page, perPage)
      } catch (err) {
        error.value = err as Error
        console.error('[useInvite] 获取提现记录失败:', err)
        return null
      } finally {
        loading.value = false
      }
    }

    /**
     * 获取邀请人信息
     */
    const getInviteInfo = async (): Promise<InviteInfo | null> => {
      loading.value = true
      error.value = null
      try {
        return await inviteService.getInviteInfo()
      } catch (err) {
        error.value = err as Error
        console.error('[useInvite] 获取邀请人信息失败:', err)
        return null
      } finally {
        loading.value = false
      }
    }

    return {
      // 状态
      rewardDetail,
      availableReward,
      frozenReward,
      totalReward,
      canWithdraw,
      loading: readonly(loading),
      error: readonly(error),

      // 方法
      getRewardDetail,
      applyWithdraw,
      getRewardList,
      getRecommendFailList,
      getRecommendUserList,
      getWithdrawRecordList,
      getInviteInfo
    }
  }
}
